import './ui/ui.css';
import { Game } from '@/core/Game';
import { Flow } from '@/core/Flow';
import { soundtrack } from '@/audio/Soundtrack';
import { mountRotateOverlay } from '@/ui/RotateOverlay';
import { dismissBootSplash } from '@/ui/bootSplash';
import { legacyRedirectTarget } from '@/config/site';
import { devHostOverrideAllowed } from '@/config/network';
import { captureReferralParam } from '@/account/referral';
import { captureSocialParams } from '@/social/links';
import { loadSave } from '@/core/SaveData';
import { isNativeApp, setNativeReady } from '@/native/shims';
import { mark, span, exposePerf } from '@/dev/perf';
import { installErrorReporting, exposeErrors } from '@/dev/errorlog';
import { installIdleGate } from '@/core/idleGate';

/**
 * main.ts — bootstrap. Create the Game (3D + race machine), hand it to the
 * Flow (menus + save + tour), and boot to the title screen.
 *
 * Dev URL params:
 *   ?seed=123      — override the loaded city's track seed (reproducible track)
 *   ?city=tokyo    — skip the menus and quick-race straight into that city
 *   ?race=1        — skip the menus, race the default city
 *   ?debug=1       — dev overlay (fps/seed/state) + the C spectate key
 *   ?net=loopback  — Phase 2 online DEMO: a local authoritative-host + predicting
 *                    -client race over a simulated network (combine with ?city=)
 *   ?lat=120&jit=25&loss=0.05 — tune the simulated link (ms, ms, fraction) for
 *                    ?net=loopback to SEE prediction/interpolation under load
 */
import type { LinkConfig } from '@/net/LoopbackLink';

const params = new URLSearchParams(window.location.search);

// M111: inside the Capacitor app shell, kick off the native plugin bridge
// FIRST (a lazy chunk the website never downloads) — registered synchronously
// so anything awaiting nativeReady() (the Supabase client's storage adapter)
// sees the real promise. The web path resolves immediately with empty shims.
if (isNativeApp()) {
  setNativeReady(import('@/native/init').then((m) => m.initNative()));
  // SPLASH FAILSAFE (measured on cold iPad sims): the very first launch can
  // fail the dynamic import above (the game still boots — setNativeReady
  // swallows it by design) and the splash, which native init normally hides,
  // then sits on screen FOREVER (launchAutoHide is false). Release it from
  // the MAIN bundle via the injected bridge global — zero imports, so this
  // path cannot be lost with the chunk. Idempotent; retried while the cold
  // first boot grinds.
  const releaseSplash = (): void => {
    try {
      const cap = (window as unknown as {
        Capacitor?: { Plugins?: { SplashScreen?: { hide?: (o: object) => unknown } } };
      }).Capacitor;
      void cap?.Plugins?.SplashScreen?.hide?.({ fadeOutDuration: 250 });
    } catch {
      /* cosmetic — never fatal */
    }
  };
  for (const t of [1200, 3000, 6000, 10000, 16000]) window.setTimeout(releaseSplash, t);
}

// M100b: a production boot on a LEGACY host (*.pages.dev) bounces to the
// canonical dribbleracer.com — one origin, one save, one identity. Un-linked
// saves with progress stay put (their SP lives in this origin's storage until
// a Racer ID carries it) and auth landings always finish locally; see
// config/site.ts. The whole boot is skipped while the browser navigates.
// Never runs natively (the app IS its own origin; nothing to bounce).
const redirectTarget =
  import.meta.env.PROD && !isNativeApp()
    ? legacyRedirectTarget(window.location.hostname, window.location.href, loadSave())
    : null;
if (redirectTarget) {
  window.location.replace(redirectTarget);
} else {
  bootGame();
}

function bootGame(): void {
  // M101: a share link (?ref=CODE) is remembered FIRST — before anything can
  // cache or rewrite the URL — and stripped; it claims for the sharer when
  // this visitor creates a Racer ID. (Legacy-host boots redirect with the
  // full query, so the canonical origin's boot lands here and captures.)
  // M140: crew links (?crew=CODE) + friend links (?ref=CODE doubles as the
  // friend link) are remembered first — the referral capture strips ?ref.
  captureSocialParams();
  captureReferralParam();
  const seedParam = params.get('seed');

  /** Read any provided link knobs (?lat/?jit/?loss); unset ones keep the defaults. */
  const readLink = (): Partial<LinkConfig> | undefined => {
    const map: [string, keyof LinkConfig][] = [
      ['lat', 'latencyMs'],
      ['jit', 'jitterMs'],
      ['loss', 'loss'],
    ];
    const out: Partial<LinkConfig> = {};
    let any = false;
    for (const [p, k] of map) {
      const v = params.get(p);
      if (v !== null && Number.isFinite(Number(v))) {
        out[k] = Number(v);
        any = true;
      }
    }
    return any ? out : undefined;
  };

  const mount = document.getElementById('app');
  if (!mount) {
    throw new Error('Dribble Racer: #app mount element not found in index.html');
  }

  // Landscape-first: a phone held in portrait gets a "rotate your phone" nudge.
  // Purely CSS-driven visibility (see ui.css); this just builds it + the escape hatch.
  mountRotateOverlay(mount);

  // M127 dev lab: `?debug=1&lab=livery` mounts the paint-job contact sheet
  // INSTEAD of the game (a lazy chunk behind the debug gate — never in play).
  if (params.has('debug') && params.get('lab') === 'livery') {
    void import('./dev/liveryLab').then((m) => m.mountLiveryLab(mount));
    return;
  }
  // M158 dev lab: `?debug=1&lab=props` — the match balls in a city's lighting.
  if (params.has('debug') && params.get('lab') === 'props') {
    void import('./dev/propsLab').then((m) => m.mountPropsLab(mount, params));
    return;
  }

  mark('boot.start');
  // Loading round: the game's first global error telemetry — an exception
  // that strands a race start used to leave NOTHING behind anywhere.
  installErrorReporting();
  installIdleGate(); // loading round 2: background work yields to the player's taps
  const game = span(
    'boot.game',
    () =>
      new Game(mount, {
        seedOverride: seedParam ? Number(seedParam) >>> 0 : undefined,
        debug: params.has('debug'),
      }),
  );
  // Dev handle (M74): with ?debug=1 the console can poke the live game — used to
  // drive hard-to-time states (e.g. the spectate presentation) during QA.
  // __soundtrack (M85) exposes the menu/transition music state the same way.
  if (params.has('debug')) {
    (window as { __game?: Game }).__game = game;
    (window as { __soundtrack?: typeof soundtrack }).__soundtrack = soundtrack;
  }
  /** An optional positive-integer URL param (invite-link room config, M45). */
  const readInt = (name: string): number | undefined => {
    const v = Number(params.get(name));
    return Number.isFinite(v) && v > 0 ? Math.round(v) : undefined;
  };

  const flow = span('boot.flow', () => new Flow(game, mount));
  if (params.has('debug')) {
    exposePerf();
    exposeErrors();
  }
  span('boot.flowBoot', () =>
    flow.boot({
    cityId: params.get('city') ?? undefined,
    autostart: params.has('race'),
    net: params.get('net') ?? undefined,
    link: readLink(),
    // M100 hardening, tightened M108b: ?host= redirects the client to an
    // ARBITRARY ws server, so it requires a DEV ORIGIN (localhost / vite
    // dev). It used to require only ?debug=1 — which a crafted link simply
    // includes, defeating the control it was meant to enforce.
    wsUrl: devHostOverrideAllowed() ? (params.get('host') ?? undefined) : undefined,
    // V2 (M106): ?h= pins a room to one of OUR hosts by allowlist KEY (never a
    // raw URL — the M100 rule holds) so an invite link lands the friend on the
    // same server the room actually lives on. Safe un-gated.
    hostKey: params.get('h') ?? undefined,
    room: params.get('room') ?? undefined,
    field: readInt('field'),
    tour: readInt('tour'),
      cities: params.get('cities') ?? undefined,
    }),
  );
  mark('boot.done');
  dismissBootSplash();

  // Installable PWA: register the offline service worker (production only —
  // in dev it would fight Vite's module server). Skipped in the app shell
  // (M111): WKWebView has no SW on capacitor://, the bundle already ships
  // inside the binary, and store releases replace the SW's update role.
  if (import.meta.env.PROD && !isNativeApp() && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('./sw.js').catch(() => {
        /* offline support is a bonus, never a blocker */
      });
    });
  }

  // Expose for quick poking from the browser console — debug builds only
  // (M100: flow now holds account/session-adjacent state; same gate as __game).
  if (params.has('debug')) {
    (window as unknown as { game: Game; flow: Flow }).game = game;
    (window as unknown as { game: Game; flow: Flow }).flow = flow;
    // M101 QA: window.__onboarding.preview() runs the post-signup welcome on
    // demand with NO server writes (the real flow fires once per new account).
    (window as unknown as { __onboarding: { preview: () => void } }).__onboarding = {
      preview: () => flow.debugOnboardingPreview(),
    };
  }
}
